import { defineComponent, computed } from "vue";
import { Container } from "aurelia-dependency-injection";
import { mapState, mapActions } from "pinia";
import { useVuelidate } from "@vuelidate/core";
import { required, requiredIf, maxLength, minLength } from "@vuelidate/validators";
import { ToastrService } from "../../../../../services/toastr.service";
import { PatientService } from "../../../../../services/patient.service";
import { useEditReferralHH } from "../../../store/useEditReferralHH";

import Medicaid from "./Medicaid/Medicaid.vue";
import Medicare from "./Medicare/Medicare.vue";
import FormSection from "../../../../../resources-vue/vue-custom-elements/FormSection/FormSection.vue";
import VerifyEligibility from "../../../../../resources-vue/vue-dialogs/VerifyEligibility/VerifyEligibility.vue";

export default defineComponent({
    components: {
        FormSection,
        Medicaid,
        Medicare
    },

    computed: {
        ...mapState(useEditReferralHH, {
            application: "currentApplication",
            referral: "referral",
            referralOptions: "referralOptions",
            accountId: "accountId",
            errors: "errors"
        })
    },
    validations() {
        return {
            referral: {
                Insurance: {
                    MbiNumber: {
                        required: requiredIf(() => !this.referral.Insurance.IsMbiNA),
                        maxLength: maxLength(11),
                        minLength: minLength(11)
                    },
                    MedicaidNumber: {
                        required: requiredIf(() => !this.referral.Insurance.IsMedicaidNA)
                    },
                    PrimaryInsurance: { required }
                }
            }
        };
    },
    provide() {
        return {
            dialogRef: computed(() => this.dialogRef)
        };
    },

    async created() {
        this._patientsService = Container.instance.get(PatientService);
        this._toastrService = Container.instance.get(ToastrService);
    },

    data() {
        return {
            medicareInsurance: ["1", "2", "3", "4", "5"],
            isPrimaryMedicare: null,
            isPrimaryMedicaid: null,
            isSecondaryMedicare: null,
            isSecondaryMedicaid: null,
            isTertiaryMedicare: null,
            isTertiaryMedicaid: null,
            dialogRef: null,
            _toastrService: null,
            _patientsService: null,
            v$: useVuelidate()
        };
    },

    mounted() {
        if (this.referral.Insurance?.PrimaryInsurance) {
            this.setMedicareOrMedicaid(this.referral.Insurance.PrimaryInsurance, "primary", true);
        }
        if (this.referral.Insurance?.PrimaryInsurance) {
            this.setMedicareOrMedicaid(this.referral.Insurance.SecondaryInsurance, "secondary", true);
        }
        if (this.referral.Insurance?.PrimaryInsurance) {
            this.setMedicareOrMedicaid(this.referral.Insurance.TertiaryInsurance, "tertiary", true);
        }
    },

    watch: {
        attendingPhysician(newValue: any) {
            let name = newValue;
            if (newValue && typeof newValue == "object") {
                name = newValue?.name?.toString();
                this.errors.Physicians = this.errors.Physicians.filter(
                    (item: string) => item !== "PrimaryCarePhysician"
                );
                this.referral.Physicians.PrimaryCarePhysician = newValue?.value;
                this.referral.Physicians.PrimaryCarePhysicianDetails.Id = newValue?.value;
                this.referral.Physicians.PrimaryCarePhysicianDetails.DisplayName = name;
            }
        }
    },
    methods: {
        ...mapActions(useEditReferralHH, { setErrors: "SET_NEW_ERRORS" }),
        setMedicareOrMedicaid(value: any, type: string, fromMounted: boolean = false) {
            if (type == "primary") {
                if (!fromMounted) this.handlePrimaryInsurance();
                if (this.isMedicare(value)) {
                    this.isPrimaryMedicare = true;
                    this.isPrimaryMedicaid = false;
                } else {
                    this.isPrimaryMedicare = false;
                    this.isPrimaryMedicaid = true;
                }
                if (this.isMedicare(value) == null) {
                    this.isPrimaryMedicare = false;
                    this.isPrimaryMedicaid = false;
                }
            }
            if (type == "secondary") {
                if (!fromMounted) this.handleSecondaryInsurance();
                if (this.isMedicare(value)) {
                    this.isSecondaryMedicare = true;
                    this.isSecondaryMedicaid = false;
                } else {
                    this.isSecondaryMedicare = false;
                    this.isSecondaryMedicaid = true;
                }
                if (this.isMedicare(value) == null) {
                    this.isSecondaryMedicare = false;
                    this.isSecondaryMedicaid = false;
                }
            }
            if (type == "tertiary") {
                if (!fromMounted) this.handleTertiaryInsurance();
                if (this.isMedicare(value)) {
                    this.isTertiaryMedicare = true;
                    this.isTertiaryMedicaid = false;
                } else {
                    this.isTertiaryMedicare = false;
                    this.isTertiaryMedicaid = true;
                }
                if (this.isMedicare(value) == null) {
                    this.isTertiaryMedicare = false;
                    this.isTertiaryMedicaid = false;
                }
            }
            this.handleAllInput("PrimaryInsurance", value);
        },
        isMedicare(value: any) {
            const insurance = this.medicareInsurance;
            if (value == null) {
                return null;
            }
            if (insurance.includes(value)) {
                return true;
            } else {
                return false;
            }
        },

        handlePrimaryInsurance() {
            this.referral.Insurance.PrimaryGroupId = "";
            this.referral.Insurance.PrimaryGroupName = "";
            this.referral.Insurance.PrimaryHealthPlanId = "";
            this.referral.Insurance.PrimaryRelationship = null;
            this.referral.Insurance.PrimaryMedicarePartADate = "";
            this.referral.Insurance.PrimaryMedicarePartBDate = "";
            this.referral.Insurance.PrimaryExistingPriorEpisode = 0;
        },
        handleSecondaryInsurance() {
            this.referral.Insurance.SecondaryGroupId = "";
            this.referral.Insurance.SecondaryGroupName = "";
            this.referral.Insurance.SecondaryHealthPlanId = "";
            this.referral.Insurance.SecondaryRelationship = null;
            this.referral.Insurance.SecondaryMedicarePartADate = "";
            this.referral.Insurance.SecondaryMedicarePartBDate = "";
            this.referral.Insurance.SecondaryExistingPriorEpisode = 0;
        },
        handleTertiaryInsurance() {
            this.referral.Insurance.TertiaryGroupId = "";
            this.referral.Insurance.TertiaryGroupName = "";
            this.referral.Insurance.TertiaryHealthPlanId = "";
            this.referral.Insurance.TertiaryRelationship = null;
            this.referral.Insurance.TertiaryMedicarePartADate = "";
            this.referral.Insurance.TertiaryMedicarePartBDate = "";
            this.referral.Insurance.TertiaryExistingPriorEpisode = 0;
        },
        handleAllInput(field: string, value: any) {
            if (value?.length || value !== null) {
                const newErrors = [...new Set(this.errors.Insurance)].filter((val: any) => val !== field);
                const payload = {
                    property: "Insurance",
                    value: newErrors
                };
                this.setErrors(payload);
            }
        },
        async verifyEligibility() {
            try {
                const missingFields = [];
                if (!this.referral.Insurance.MbiNumber) missingFields.push("MbiNumber");
                if (!this.referral.Demographics.LastName) missingFields.push("LastName");
                if (!this.referral.Demographics.FirstName) missingFields.push("FirstName");
                if (!this.referral.Demographics.DOB) missingFields.push("Date of birth");
                if (!this.referral.Demographics.Gender) missingFields.push("gender");
                if (missingFields.length > 0) {
                    const displayedFields = missingFields.map((item) => `•${item}`).join("<br>");
                    this._toastrService.error({
                        title: "Missing Required Fields",
                        message: `Please provide the following before verifying eligibility:<br>${displayedFields}`
                    });
                    return;
                }
                const response = await this._patientsService.getverifyEligibility({
                    agencyId: this.referral.AgencyId,
                    medicareNumber: this.referral.Insurance.MbiNumber,
                    lastName: this.referral.Demographics.LastName,
                    firstName: this.referral.Demographics.FirstName,
                    dob: this.referral.Demographics.DOB,
                    gender: this.referral.Demographics.Gender
                });
                this.dialogRef = this.$dialog.open(VerifyEligibility, {
                    props: {
                        modal: true,
                        showHeader: false,
                        style: "width: 950px"
                    },
                    data: {
                        response,
                        AgencyId: this.referral.AgencyId
                    }
                });
            } catch (error) {
                this._toastrService.error({
                    title: `Error`,
                    message: `There was a problem while verifying this referral's eligibility. Please try again.`
                });
            }
        }
    }
});
